import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Input,
    Card,
    Drawer,
    Form,
    Select,
    InputNumber,
    notification,
    Popconfirm,
    Tag,
    Tooltip
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    SearchOutlined,
    BarcodeOutlined,
    AppstoreOutlined
} from '@ant-design/icons';
import { itemService } from '../services/itemService';

const { Option } = Select;
const { Search } = Input;

const ProductList = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [isDrawerVisible, setIsDrawerVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [form] = Form.useForm();

    const fetchItems = async (search = '') => {
        try {
            setLoading(true);
            const response = await itemService.getAllItems({ search });
            if (response.success) {
                setItems(response.data || []);
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch items'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
    }, []);

    const handleSearch = (value) => {
        setSearchText(value);
        fetchItems(value);
    };

    const handleAdd = () => {
        setEditingItem(null);
        form.resetFields();
        setIsDrawerVisible(true);
    };

    const handleEdit = (record) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setIsDrawerVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await itemService.deleteItem(id);
            notification.success({
                message: 'Success',
                description: 'Item deleted successfully'
            });
            fetchItems(searchText);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || 'Failed to delete item'
            });
        }
    };

    const onFinish = async (values) => {
        try {
            if (editingItem) {
                await itemService.updateItem(editingItem.id, values);
                notification.success({
                    message: 'Success',
                    description: 'Item updated successfully'
                });
            } else {
                await itemService.createItem(values);
                notification.success({
                    message: 'Success',
                    description: 'Item created successfully'
                });
            }
            setIsDrawerVisible(false);
            fetchItems(searchText);
        } catch (error) {
            notification.error({
                message: 'Error',
                description: error.response?.data?.error || 'Operation failed'
            });
        }
    };

    const columns = [
        {
            title: 'Item Name',
            dataIndex: 'itemName',
            key: 'itemName',
            sorter: (a, b) => a.itemName.localeCompare(b.itemName),
            render: (text, record) => (
                <Space>
                    <AppstoreOutlined style={{ color: '#1890ff' }} />
                    <span style={{ fontWeight: 500 }}>{text}</span>
                </Space>
            )
        },
        {
            title: 'Type',
            dataIndex: 'itemType',
            key: 'itemType',
            filters: [
                { text: 'Solar Panel', value: 'SolarPanel' },
                { text: 'Inverter', value: 'Inverter' },
                { text: 'General', value: 'General' },
                { text: 'Service', value: 'Service' }
            ],
            onFilter: (value, record) => record.itemType === value,
            render: (type) => {
                let color = 'default';
                if (type === 'SolarPanel') color = 'blue';
                if (type === 'Inverter') color = 'cyan';
                return <Tag color={color}>{type}</Tag>;
            }
        },
        {
            title: 'Model No',
            dataIndex: 'modelNo',
            key: 'modelNo',
        },
        {
            title: 'HSN Code',
            dataIndex: 'hsnCode',
            key: 'hsnCode',
        },
        {
            title: 'Warranty (Years)',
            dataIndex: 'warrantyMonths',
            key: 'warrantyMonths',
            align: 'center',
        },
        {
            title: 'Tax Rate (%)',
            dataIndex: 'defaultTaxRate',
            key: 'defaultTaxRate',
            align: 'right',
            render: (val) => `${val}%`
        },
        {
            title: 'Actions',
            key: 'actions',
            align: 'center',
            render: (text, record) => (
                <Space>
                    <Tooltip title="Edit">
                        <Button
                            type="text"
                            icon={<EditOutlined style={{ color: '#fa8c16' }} />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete Item"
                        description="Are you sure? This cannot be undone."
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    return (
        <div className="p-6">
            <Card
                title={
                    <div className="flex items-center gap-2">
                        <AppstoreOutlined />
                        <span>Product Definitions (Items)</span>
                    </div>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAdd}
                    >
                        Add New Item
                    </Button>
                }
            >
                <div className="mb-4 flex justify-between items-center">
                    <Search
                        placeholder="Search items..."
                        allowClear
                        enterButton={<SearchOutlined />}
                        onSearch={handleSearch}
                        className="max-w-md"
                    />
                </div>

                <Table
                    columns={columns}
                    dataSource={items}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </Card>

            <Drawer
                title={editingItem ? "Edit Item" : "New Item"}
                width={500}
                onClose={() => setIsDrawerVisible(false)}
                open={isDrawerVisible}
                maskClosable={false}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        warrantyMonths: 0,
                        defaultTaxRate: 18,
                        itemType: 'General'
                    }}
                >
                    <Form.Item
                        name="itemName"
                        label="Item Name"
                        rules={[{ required: true, message: 'Please enter item name' }]}
                    >
                        <Input placeholder="e.g. Monocrystalline Solar Panel 540W" />
                    </Form.Item>

                    <Form.Item
                        name="itemType"
                        label="Item Type"
                        rules={[{ required: true, message: 'Please select item type' }]}
                    >
                        <Select
                            onChange={(value) => {
                                const taxRate = (value === 'SolarPanel' || value === 'Inverter') ? 5 : 18;
                                form.setFieldsValue({ defaultTaxRate: taxRate });
                            }}
                        >
                            <Option value="SolarPanel">Solar Panel</Option>
                            <Option value="Inverter">Inverter</Option>
                            <Option value="General">General (Cables, Structures, etc.)</Option>
                            <Option value="Service">Service (Installation, Supply, etc.)</Option>
                        </Select>
                    </Form.Item>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                            name="modelNo"
                            label="Model Number"
                        >
                            <Input placeholder="Optional" />
                        </Form.Item>

                        <Form.Item
                            name="hsnCode"
                            label="HSN Code"
                        >
                            <Input placeholder="Optional" />
                        </Form.Item>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Form.Item
                            name="warrantyMonths"
                            label="Warranty (Years)"
                            rules={[{ required: true }]}
                        >
                            <InputNumber min={0} className="w-full" />
                        </Form.Item>

                        <Form.Item
                            name="defaultTaxRate"
                            label="Default Tax Rate (%)"
                            rules={[{ required: true }]}
                        >
                            <InputNumber min={0} max={28} className="w-full" />
                        </Form.Item>
                    </div>

                    <div className="flex justify-end gap-3 mt-6">
                        <Button onClick={() => setIsDrawerVisible(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit">
                            {editingItem ? 'Update' : 'Create'}
                        </Button>
                    </div>
                </Form>
            </Drawer>
        </div>
    );
};

export default ProductList;
